import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Building2, LifeBuoy, Paperclip } from 'lucide-react';
import { Page, PageHeader } from '@ampm-7en-ai/ui/components/Page';
import { Panel } from '@ampm-7en-ai/ui/components/Panel';
import { Avatar } from '@ampm-7en-ai/ui/components/Avatar';
import { IconChip } from '@ampm-7en-ai/ui/components/IconChip';
import { DetailRow, DetailRows, DetailSection } from '@ampm-7en-ai/ui/components/DetailList';
import { Field } from '@ampm-7en-ai/ui/components/Input';
import { Textarea } from '@ampm-7en-ai/ui/components/Textarea';
import { Select } from '@ampm-7en-ai/ui/components/Select';
import { Button } from '@ampm-7en-ai/ui/components/Button';
import { Callout } from '@ampm-7en-ai/ui/components/Callout';
import { Pill } from '@ampm-7en-ai/ui/components/StatusBadge';
import { Skeleton } from '@ampm-7en-ai/ui/components/Skeleton';
import {
  SUPPORT_STATUSES, useSupportIssue, useUpdateSupportIssue,
  type SupportAttachment, type SupportIssue, type SupportStatus,
} from '@/hooks/useSupportIssues';
import { useToast } from '@/hooks/use-toast';
import { formatRelativeTime } from '@/lib/utils';

/**
 * One reported problem: what was reported, who reported it, and what has been
 * done about it. The report leads, because that is what an admin opens the page
 * to read; the reporter and the audit trail sit beside it as reference rather
 * than as one flat list to scan.
 */

const STATUS_TONE: Record<SupportStatus, 'warning' | 'accent' | 'success' | 'neutral'> = {
  OPEN: 'warning', IN_PROGRESS: 'accent', RESOLVED: 'success', CLOSED: 'neutral',
};

const statusLabel = (status: SupportStatus) =>
  SUPPORT_STATUSES.find((entry) => entry.value === status)?.label ?? status;

const date = (value?: string | null) => {
  if (!value) return 'Not recorded';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0]?.toUpperCase() ?? '').join('') || '?';

const IMAGE_FILE = /\.(png|jpe?g|gif|webp|avif|bmp|svg)$/i;

/** Screenshots are the common case, so show them rather than just naming them. */
function AttachmentRow({ file }: { file: SupportAttachment }) {
  const [broken, setBroken] = useState(false);
  let isImage = false;
  try {
    isImage = IMAGE_FILE.test(new URL(file.url).pathname);
  } catch {
    // Treated as a plain file when the address tells us nothing about its type.
  }

  return (
    <li className="flex items-center gap-3 rounded-ui-md border border-ui-border p-2">
      {isImage && !broken ? (
        <a href={file.url} target="_blank" rel="noopener noreferrer" tabIndex={-1} aria-hidden>
          <img src={file.url} alt="" loading="lazy" referrerPolicy="no-referrer"
            className="h-12 w-12 rounded-ui-sm border border-ui-border object-cover"
            onError={() => setBroken(true)} />
        </a>
      ) : (
        <IconChip icon={Paperclip} tone="neutral" />
      )}
      <span className="min-w-0 flex-1 truncate text-ui-sm text-ui-fg" title={file.name}>{file.name}</span>
      <Button variant="ghost" size="sm" asChild>
        <a href={file.url} target="_blank" rel="noopener noreferrer">
          View attachment<span className="sr-only"> {file.name} (opens in a new tab)</span>
        </a>
      </Button>
    </li>
  );
}

function IssueEditor({ issue }: { issue: SupportIssue }) {
  const [saved, setSaved] = useState(issue);
  const [status, setStatus] = useState<SupportStatus>(issue.status);
  const [notes, setNotes] = useState(issue.admin_notes);
  const [failure, setFailure] = useState('');
  const update = useUpdateSupportIssue();
  const { toast } = useToast();
  const dirty = status !== saved.status || notes !== saved.admin_notes;
  const attachments = issue.attachments ?? [];
  const unusable = issue.unusableAttachments ?? 0;
  const reporter = issue.reporter_name || issue.reporter_email || null;

  useEffect(() => {
    if (!dirty && !update.isPending) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, update.isPending]);

  const save = async () => {
    setFailure('');
    const fields: Partial<Pick<SupportIssue, 'status' | 'admin_notes'>> = {};
    if (status !== saved.status) fields.status = status;
    if (notes !== saved.admin_notes) fields.admin_notes = notes;
    try {
      const result = await update.mutateAsync({ id: issue.id, fields });
      setSaved(result);
      setStatus(result.status);
      setNotes(result.admin_notes);
      toast({ title: 'Support issue saved' });
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'The support issue could not be saved.');
    }
  };

  return (
    <>
      <Link to="/v2/support-issues"
        className="-ml-1 mb-3 inline-flex items-center gap-1.5 text-ui-sm text-ui-fg-muted hover:text-ui-fg"
        onClick={(event) => {
          if (update.isPending || (dirty && !window.confirm('Discard unsaved changes to this issue?'))) event.preventDefault();
        }}><ArrowLeft className="h-4 w-4" aria-hidden />Back to support issues</Link>

      <PageHeader
        // A subject is the page's content, not a label: let it wrap rather than truncate.
        className="[&_h1]:whitespace-normal"
        title={issue.subject}
        meta={<Pill tone={STATUS_TONE[saved.status]}>{statusLabel(saved.status)}</Pill>}
        description={`Issue #${issue.id} · Reported ${formatRelativeTime(issue.created_at).toLowerCase()}`}
      />

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-4">
          <Panel padding="md" className="min-w-0">
            <h2 className="mb-3 text-ui-base font-medium text-ui-fg">Reported problem</h2>
            <p className="whitespace-pre-wrap break-words text-ui-sm leading-relaxed text-ui-fg">{issue.description}</p>
          </Panel>

          {(attachments.length > 0 || unusable > 0) && (
            <Panel padding="md" className="min-w-0">
              <h2 className="mb-3 flex items-center gap-2 text-ui-base font-medium text-ui-fg">
                {attachments.length === 1 ? 'Attachment' : 'Attachments'}
                {attachments.length > 1 && <Pill>{attachments.length}</Pill>}
              </h2>
              {attachments.length > 0 && (
                <ul className="space-y-2">
                  {attachments.map((file) => <AttachmentRow key={file.url} file={file} />)}
                </ul>
              )}
              {unusable > 0 && (
                <Callout tone="warning" className={attachments.length ? 'mt-3' : undefined} title="Attachment unavailable">
                  <p>{unusable === 1
                    ? 'One upload on this report has no usable web address, so it cannot be opened from here.'
                    : `${unusable} uploads on this report have no usable web address, so they cannot be opened from here.`}</p>
                </Callout>
              )}
            </Panel>
          )}
        </div>

        <div className="min-w-0 space-y-4">
          <Panel padding="md" className="min-w-0">
            <form className="space-y-5" onSubmit={(event) => { event.preventDefault(); if (dirty && !update.isPending) void save(); }}>
              <div>
                <h2 className="text-ui-base font-medium text-ui-fg">Manage issue</h2>
                <p className="mt-1 text-ui-xs text-ui-fg-muted">Move the issue through the workflow and record what was done.</p>
              </div>
              {failure && <Callout tone="danger" title="Could not save issue"><p>{failure}</p></Callout>}
              <Field label="Status" htmlFor="support-status">
                <Select id="support-status" options={[...SUPPORT_STATUSES]} value={status} disabled={update.isPending}
                  onValueChange={(value) => {
                    const selected = SUPPORT_STATUSES.find((entry) => entry.value === value);
                    if (selected) setStatus(selected.value);
                  }} />
              </Field>
              <Field label="Admin notes" htmlFor="admin-notes" hint="Internal notes, visible to superadmins only.">
                <Textarea id="admin-notes" rows={8} value={notes} onChange={(event) => setNotes(event.target.value)} disabled={update.isPending} />
              </Field>
              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-ui-border pt-4">
                {dirty && <span className="mr-auto text-ui-xs text-ui-fg-muted">Unsaved changes</span>}
                <Button type="submit" variant="primary" disabled={!dirty || update.isPending}>
                  {update.isPending ? 'Saving...' : 'Save changes'}
                </Button>
              </div>
            </form>
          </Panel>

          <Panel padding="none" className="min-w-0">
            <DetailSection title="Reporter">
              <div className="mb-3 flex items-center gap-3">
                <Avatar initials={reporter ? initials(reporter) : undefined}
                  icon={reporter ? undefined : <LifeBuoy className="h-4 w-4" aria-hidden />} />
                <div className="min-w-0">
                  <p className="truncate text-ui-sm font-medium text-ui-fg">{issue.reporter_name || 'Name not provided'}</p>
                  <p className="flex items-center gap-1 truncate text-ui-xs text-ui-fg-muted">
                    <Building2 className="h-3 w-3 shrink-0" aria-hidden />{issue.business_name || 'No business recorded'}
                  </p>
                </div>
              </div>
              <DetailRows>
                <DetailRow label="Email" wrap>
                  {issue.reporter_email
                    ? <a className="text-ui-accent-text hover:underline" href={`mailto:${issue.reporter_email}`}>{issue.reporter_email}</a>
                    : 'Not provided'}
                </DetailRow>
              </DetailRows>
            </DetailSection>

            <DetailSection title="Timeline">
              <DetailRows>
                <DetailRow label="Reported" wrap>{date(issue.created_at)}</DetailRow>
                <DetailRow label="Updated" wrap>{date(saved.updated_at)}</DetailRow>
                <DetailRow label="Resolved" wrap>{date(saved.resolved_at)}</DetailRow>
              </DetailRows>
            </DetailSection>

            <DetailSection title="Status history">
              <DetailRows>
                <DetailRow label="Current status">
                  <Pill tone={STATUS_TONE[saved.status]}>{statusLabel(saved.status)}</Pill>
                </DetailRow>
                <DetailRow label="Last status change by" wrap>{saved.status_changed_by || 'Not recorded'}</DetailRow>
                <DetailRow label="Last status change" wrap>{date(saved.status_changed_at)}</DetailRow>
              </DetailRows>
            </DetailSection>
          </Panel>
        </div>
      </div>
    </>
  );
}

export default function SupportIssueDetailV2() {
  const { issueId } = useParams();
  const { data, isLoading, isError, error, refetch } = useSupportIssue(issueId);
  return <Page>
    {isLoading ? (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
          <Skeleton className="h-80 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    ) : isError ? (
      <>
        <Link to="/v2/support-issues" className="mb-4 inline-block text-ui-sm text-ui-accent-text">Back to support issues</Link>
        <Callout tone="danger" title="Could not load support issue">
          <p>{error instanceof Error ? error.message : 'The request failed.'}</p>
          <Button className="mt-3" onClick={() => void refetch()}>Try again</Button>
        </Callout>
      </>
    ) : data ? <IssueEditor key={data.id} issue={data} /> : null}
  </Page>;
}
